import { Button, Tag } from 'antd';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { Organization } from 'protocol/model/dto_organization_pb';

import { OrganizationParentDSO } from '../OrganizationParentDSO/OrganizationParentDSO';

import styles from './OrganizationDSOSection.module.scss';

/**
 * An organization occupies exactly one place in the DSO hierarchy, and the row
 * rendered for it differs per place:
 * - `dso` — it is a DSO itself, so it can only lose that status;
 * - `parent` — it belongs to a DSO (or the parent is being picked right now);
 * - `unassigned` — neither, so both roles are still offered.
 */
type OrganizationDSOState = 'dso' | 'parent' | 'unassigned';

type OrganizationDSOSectionProps = {
  canManageDSO: boolean;
  canManageHierarchy: boolean;
  isDSO: boolean;
  isParentEditorVisible: boolean;
  organizationID: string;
  parentID: string;
  parentEmail?: string;
  parentName?: string;
  parentOrganization?: Organization;
  onMarkAsDSO: () => void;
  onParentEditorVisibleChange: (visible: boolean) => void;
  onUnmarkAsDSO: () => void;
};

const getDSOState = (params: {
  isDSO: boolean;
  isParentEditorVisible: boolean;
  parentID: string;
}): OrganizationDSOState => {
  const { isDSO, isParentEditorVisible, parentID } = params;

  if (isDSO) {
    return 'dso';
  }

  if (parentID || isParentEditorVisible) {
    return 'parent';
  }

  return 'unassigned';
};

export const OrganizationDSOSection: FC<OrganizationDSOSectionProps> = (
  props,
) => {
  const {
    canManageDSO,
    canManageHierarchy,
    isDSO,
    isParentEditorVisible,
    organizationID,
    parentID,
    parentEmail,
    parentName,
    parentOrganization,
    onMarkAsDSO,
    onParentEditorVisibleChange,
    onUnmarkAsDSO,
  } = props;

  const dsoState = getDSOState({ isDSO, isParentEditorVisible, parentID });

  const showParentEditor = () => onParentEditorVisibleChange(true);

  if (dsoState === 'dso') {
    return (
      <>
        <span className={styles.label}>
          <FormattedMessage id="organization.isDSO" defaultMessage="Is DSO" />
        </span>
        <span className={styles.tagCell}>
          <Tag color="blue">
            <FormattedMessage id="organization.dsoTag" defaultMessage="DSO" />
          </Tag>

          <Button
            type="default"
            danger
            size="small"
            disabled={!canManageDSO}
            onClick={onUnmarkAsDSO}
          >
            <FormattedMessage
              id="organization.unmarkAsDSO"
              defaultMessage="Remove DSO Status"
            />
          </Button>
        </span>
      </>
    );
  }

  if (dsoState === 'parent') {
    return (
      <OrganizationParentDSO
        canManageParent={canManageHierarchy}
        isEditorVisible={isParentEditorVisible}
        organizationID={organizationID}
        parentID={parentID}
        parentEmail={parentEmail}
        parentName={parentName}
        parentOrganization={parentOrganization}
        onEditorVisibleChange={onParentEditorVisibleChange}
      />
    );
  }

  return (
    <>
      <span className={styles.label}>
        <FormattedMessage id="organization.dso" defaultMessage="DSO" />
      </span>
      <span className={styles.actions}>
        <Button
          type="primary"
          disabled={!canManageHierarchy}
          onClick={onMarkAsDSO}
        >
          <FormattedMessage
            id="organization.markAsDSO"
            defaultMessage="Mark as DSO"
          />
        </Button>

        <Button
          type="primary"
          disabled={!canManageHierarchy}
          onClick={showParentEditor}
        >
          <FormattedMessage
            id="organization.parent.button.assignParent"
            defaultMessage="Assign parent DSO"
          />
        </Button>
      </span>
    </>
  );
};
